import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import TemplatePicker from "components/admin/TemplatePicker";
import { formatNumber } from "utils/format";
import { NumField } from "layouts/simulator/fields";

const MAIN_STAT_LABEL = { str: "Sức", agi: "Nhanh", int: "Trí" };
const STAT_INDEX = { str: 0, agi: 1, int: 2 };

/** Ba chỉ số theo đúng thứ tự của mảng `tiemnang`: [sức, nhanh, trí]. */
const STATS = [
  { key: "str", label: "Sức" },
  { key: "agi", label: "Nhanh" },
  { key: "int", label: "Trí" },
];

/** Dồn toàn bộ điểm vào một chỉ số. */
export function allInto(stat, total) {
  const next = [0, 0, 0];
  next[STAT_INDEX[stat]] = total;
  return next;
}

/** Chia đều ba chỉ số; phần dư lẻ cho chỉ số chính của phái để không bị mất điểm. */
export function spreadEvenly(total, mainStat) {
  const each = Math.floor(total / 3);
  const next = [each, each, each];
  next[STAT_INDEX[mainStat] ?? 0] += total - each * 3;
  return next;
}

/** Một trong ba ô chia điểm. Chỉ số chính của phái quyết định công nên được ghi chú riêng. */
function PointField({ label, index, build, onChange, isMain }) {
  return (
    <Grid item xs={4}>
      <NumField
        label={label}
        value={build.tiemnang[index]}
        onChange={(value) => {
          const tiemnang = [...build.tiemnang];
          tiemnang[index] = value;
          onChange({ ...build, tiemnang });
        }}
        helper={isMain ? "chỉ số chính" : null}
      />
    </Grid>
  );
}

PointField.defaultProps = { isMain: false };

PointField.propTypes = {
  label: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  build: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  isMain: PropTypes.bool,
};

/**
 * Chọn pet và chia điểm tiềm năng.
 *
 * Pet là một MẪU PET có trong cơ sở dữ liệu (bảng `pet`), không liên quan tới tài khoản nào:
 * bộ giả lập dựng một con pet mới trong RAM từ mẫu đó.
 */
function PetBasics({ build, result, meta, onChange }) {
  const pet = result ? result.pet : null;
  const points = result ? result.points : null;
  const mainStat = pet ? MAIN_STAT_LABEL[pet.mainStat] : null;

  return (
    <MDBox>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <TemplatePicker
            kind="pet"
            size="small"
            label="Pet (chọn trong cơ sở dữ liệu)"
            value={build.petTemplateId}
            onChange={(petTemplateId) => onChange({ ...build, petTemplateId })}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <NumField
            label="Cấp"
            value={build.lvl}
            min={1}
            max={meta.maxPetLevel}
            onChange={(lvl) => onChange({ ...build, lvl })}
            helper={`tối đa ${meta.maxPetLevel}`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <NumField
            label="Điểm / cấp"
            value={build.pointPerLevel || (pet ? pet.pointPerLevel : 0)}
            min={0}
            onChange={(pointPerLevel) => onChange({ ...build, pointPerLevel })}
            helper="gymUpLevel của mẫu"
          />
        </Grid>
      </Grid>

      {pet && (
        <MDBox display="flex" gap={0.5} flexWrap="wrap" mt={1.5}>
          <Chip size="small" color="info" label={pet.className} />
          <Chip size="small" variant="outlined" label={`Chỉ số chính: ${mainStat}`} />
          <Chip
            size="small"
            variant="outlined"
            label={`Gốc ${pet.baseStr} / ${pet.baseAgi} / ${pet.baseInt}`}
          />
        </MDBox>
      )}

      <MDBox display="flex" justifyContent="space-between" alignItems="baseline" mt={2.5} mb={1}>
        <MDTypography variant="button" fontWeight="bold">
          Điểm tiềm năng
        </MDTypography>
        {points && (
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={points.left < 0 ? "error" : "text"}
          >
            {`đã chia ${formatNumber(points.used)} / ${formatNumber(
              points.total
            )} — còn ${formatNumber(points.left)}`}
          </MDTypography>
        )}
      </MDBox>
      <Grid container spacing={1}>
        <PointField
          label="Sức"
          index={0}
          build={build}
          onChange={onChange}
          isMain={pet?.mainStat === "str"}
        />
        <PointField
          label="Nhanh"
          index={1}
          build={build}
          onChange={onChange}
          isMain={pet?.mainStat === "agi"}
        />
        <PointField
          label="Trí"
          index={2}
          build={build}
          onChange={onChange}
          isMain={pet?.mainStat === "int"}
        />
      </Grid>
      {points && pet && (
        <MDBox mt={1.5}>
          {points.total <= 0 ? (
            <MDTypography variant="caption" color="warning" fontWeight="medium">
              {`Pet cấp ${build.lvl} chưa có điểm tiềm năng nào (điểm chỉ nhận được khi lên cấp).
                Tăng cấp lên để chia điểm.`}
            </MDTypography>
          ) : (
            <>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                {`Dồn cả ${formatNumber(points.total)} điểm vào:`}
              </MDTypography>
              <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center">
                {STATS.map((stat) => (
                  <MDButton
                    key={stat.key}
                    size="small"
                    variant={pet.mainStat === stat.key ? "gradient" : "outlined"}
                    color="info"
                    onClick={() =>
                      onChange({ ...build, tiemnang: allInto(stat.key, points.total) })
                    }
                  >
                    {stat.label}
                    {pet.mainStat === stat.key ? " ★" : ""}
                  </MDButton>
                ))}
                <MDButton
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() =>
                    onChange({ ...build, tiemnang: spreadEvenly(points.total, pet.mainStat) })
                  }
                >
                  Chia đều
                </MDButton>
                <MDButton
                  size="small"
                  variant="text"
                  color="error"
                  onClick={() => onChange({ ...build, tiemnang: [0, 0, 0] })}
                >
                  Xoá
                </MDButton>
              </MDBox>
              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                ★ là chỉ số chính của phái — chỉ số quyết định công.
              </MDTypography>
            </>
          )}
        </MDBox>
      )}
    </MDBox>
  );
}

PetBasics.defaultProps = { result: null };

PetBasics.propTypes = {
  build: PropTypes.object.isRequired,
  result: PropTypes.object,
  meta: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PetBasics;
